/**
 * 広島県 名物めし 12段階（コンテンツデータ）。
 *
 * 数値（radius / mass / score / unlockScore）は福岡・北海道・山形と同じテンプレート。
 * 進行の軸は「物理的な大きさ ＝ ごちそう度」で、県ごとに触らない。
 *
 * 調査で出てきた「書いてはいけないこと」を反映している。
 *  - 呼称は「広島お好み焼き」。統一された公式呼称は存在せず、「広島焼き」は公式媒体で
 *    使用例を確認できなかったので使わない。
 *  - 「お好み焼きは一銭洋食が発祥」は伝聞形。断定しない。
 *  - 尾道ラーメン・汁なし担担麺・もみじ饅頭の発祥年／発祥店は公式で裏が取れない。書かない。
 *  - 「肉じゃが発祥の地・呉」は断定不可。採用しない。
 *  - 呉のカレーの公式名称は「呉海自カレー」。「海軍カレー」は呉市公式で確認できない。
 *  - レモンは「広島県が日本一」。瀬戸田単独を日本一と書かない。
 *  - かきの土手鍋の名の由来は3説。どれにも断定しない。
 *
 * 本番画像は public/assets/foods/hiroshima/ に置く。
 */

import { attachHulls } from '../../utils/itemShape';
import type { RewardLink, StageDefinition, StageItemDefinition } from '../types';

import { HIROSHIMA_FOOD_HULLS } from './hiroshimaFoodShapes.generated';
import { hiroshimaSceneryTour } from './hiroshimaScenes';

/** 「食べに行く」導線の既定リンク（特定店の宣伝にならないよう検索リンクにする）。 */
function searchLinks(area: string, dish: string): RewardLink[] {
  return [
    {
      label: '地図で探す',
      url: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${area} ${dish}`)}`,
    },
    {
      label: 'お店を調べる',
      url: `https://www.google.com/search?q=${encodeURIComponent(`${area} ${dish}`)}`,
    },
  ];
}

const FOOD_ITEMS: StageItemDefinition[] = [
  {
    id: 'hiroshima-lemon',
    level: 1,
    name: '瀬戸内 広島レモン',
    shortName: 'レモン',
    image: '/assets/foods/hiroshima/01_hiroshima_lemon.webp',
    radius: 17,
    mass: 1,
    score: 2,
    description: '苗木3本の混入から始まった。',
    source: 'https://www.pref.hiroshima.lg.jp/site/kazyuyasaikaki/setoushihiroshimalemon.html',
    icon: { label: 'レモン', shape: 'berry', baseColor: '#e8cf3c', accentColor: '#7f9a48' },
    reward: {
      unlockScore: 200,
      area: '呉・尾道・大崎上島',
      headline: '令和3年の生産量は4,401トン、全国シェア51パーセントで日本一。',
      tip: '明治31年、大長村（いまの呉市豊町大長）が和歌山県からネーブルの苗を買ったとき、まぎれこんでいたレモンの苗3本を試しに植えたのが始まり。',
      links: searchLinks('広島県', '瀬戸内レモン'),
    },
  },
  {
    id: 'momiji-manju',
    level: 2,
    name: 'もみじ饅頭',
    shortName: 'もみじ饅頭',
    image: '/assets/foods/hiroshima/02_momiji_manju.webp',
    radius: 21,
    mass: 1.3,
    score: 4,
    description: 'もみじの葉の形に焼く。',
    source: 'https://www.miyajima.or.jp/present/present_momiman.html',
    icon: { label: 'もみじ', shape: 'mochi', baseColor: '#d98a3c', accentColor: '#8c4a24' },
    reward: {
      unlockScore: 500,
      area: '宮島',
      headline: '明治の後期、和菓子職人の高津常助が、紅葉谷の旅館の女将の依頼で「紅葉形焼饅頭」として考え出した。',
      tip: 'はじめはカステラ生地にあん入りだけだったが、いまはチョコやチーズ、クリームなど数百種類を超える。',
      links: searchLinks('廿日市市宮島町', 'もみじ饅頭'),
    },
  },
  {
    id: 'hiroshimana-musubi',
    level: 3,
    name: '広島菜むすび',
    shortName: '広島菜むすび',
    image: '/assets/foods/hiroshima/03_hiroshimana_musubi.webp',
    radius: 26,
    mass: 1.7,
    score: 8,
    description: '緑の葉で、ご飯をくるむ。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/42_23_hiroshima.html',
    icon: { label: '広島菜', shape: 'rice-bowl', baseColor: '#4d6b3a', accentColor: '#f3ecd8' },
    reward: {
      unlockScore: 1000,
      area: '広島市川内',
      headline: '本場は広島市の川内地区。古川と太田川に挟まれた水はけのよい土で、古くから野菜作りがさかん。',
      tip: '1933年の産業博覧会での展示をきっかけに品種が統一され、「広島菜」と名づけられたといわれる。浅漬けは鮮やかな緑でわさびのような香り、古漬けは乳酸発酵の酸味とうま味。',
      links: searchLinks('広島市', '広島菜漬'),
    },
  },
  {
    id: 'gansu',
    level: 4,
    name: 'がんす',
    shortName: 'がんす',
    image: '/assets/foods/hiroshima/04_gansu.webp',
    radius: 30,
    mass: 2.2,
    score: 16,
    description: 'すり身にパン粉をつけて揚げる。',
    source: 'https://www.city.hiroshima.lg.jp/hiroshimabrand/hiroshima-brand/295129.html',
    icon: { label: 'がんす', shape: 'mochi', baseColor: '#d8a45c', accentColor: '#8c5a24' },
    reward: {
      unlockScore: 1800,
      area: '広島・呉',
      headline: '魚のすり身にパン粉をつけて揚げた蒲鉾。名前の「がんす」は「〜ございます」を意味する広島弁。',
      tip: '昭和20年代、蒲鉾を作るときに出たすり身の余りを活かして生まれた。呉市の観光サイトは「呉地方で昔から食卓に並ぶ」と紹介している。',
      links: searchLinks('広島県', 'がんす'),
    },
  },
  {
    id: 'shirunashi-tantanmen',
    level: 5,
    name: '汁なし担担麺',
    shortName: '汁なし担担麺',
    image: '/assets/foods/hiroshima/05_shirunashi_tantanmen.webp',
    radius: 36,
    mass: 2.8,
    score: 32,
    description: '食べる前に、20回から30回まぜる。',
    source: 'https://dive-hiroshima.com/feature/noodle-tantanmen/',
    icon: { label: '担担麺', shape: 'noodle-bowl', baseColor: '#b5432f', accentColor: '#e8d8b8' },
    reward: {
      unlockScore: 3000,
      area: '広島市',
      headline: '中国山椒（花椒）のしびれと、ラー油の辛さ。やや低加水の細麺に、肉みそ・青ねぎ・温泉卵。',
      tip: '食べる前に20〜30回まぜて、タレが麺と具にからんだころが食べどき。食べ終わって残ったタレにごはんを入れるのを「担担飯」と呼ぶ。',
      links: searchLinks('広島市', '汁なし担担麺'),
    },
  },
  {
    id: 'onomichi-ramen',
    level: 6,
    name: '尾道ラーメン',
    shortName: '尾道ラーメン',
    image: '/assets/foods/hiroshima/06_onomichi_ramen.webp',
    radius: 42,
    mass: 3.5,
    score: 64,
    description: '醤油スープに、豚の背脂が浮く。',
    source: 'https://dive-hiroshima.com/feature/feature-117415/',
    icon: { label: '尾道', shape: 'ramen', baseColor: '#5c3a22', accentColor: '#e8d8b8' },
    reward: {
      unlockScore: 4500,
      area: '尾道',
      headline: '鶏ガラといわしなど魚介のだしを合わせた醤油ベースのスープに、豚の背脂が浮かぶ。',
      tip: '平打ちのコシのある麺に、あっさりしたチャーシュー・メンマ・青ねぎ。地元では「中華そば」と呼ぶ店が多い。',
      links: searchLinks('尾道市', '尾道ラーメン'),
    },
  },
  {
    id: 'kure-kaiji-curry',
    level: 7,
    name: '呉海自カレー',
    shortName: '呉海自カレー',
    image: '/assets/foods/hiroshima/07_kure_kaiji_curry.webp',
    radius: 46,
    mass: 4.4,
    score: 128,
    description: '艦の味を、店で再現する。',
    source: 'https://kureto.city.kure.lg.jp/read/citizen-journal/1381/',
    icon: { label: 'カレー', shape: 'curry-plate', baseColor: '#a8752f', accentColor: '#f3e6c6' },
    reward: {
      unlockScore: 6500,
      area: '呉',
      headline: '海上自衛隊呉基地の艦船などで食べられているカレーを、市内の飲食店で出す取り組み。2014年に呉市と海上自衛隊の協力で始まった。',
      tip: '認定には4つの条件があり、海上自衛隊の調理員が店へ行って実地で指導し、司令や艦艇長が「これはうちのカレーだ」と味を認めたものだけが認定される。令和5年度は21店舗。',
      links: searchLinks('呉市', '呉海自カレー'),
    },
  },
  {
    id: 'hiroshima-okonomiyaki',
    level: 8,
    name: '広島お好み焼き',
    shortName: 'お好み焼き',
    image: '/assets/foods/hiroshima/08_hiroshima_okonomiyaki.webp',
    radius: 51,
    mass: 5.4,
    score: 256,
    description: '混ぜずに重ねて、蒸し焼きにする。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/42_7_hiroshima.html',
    icon: { label: 'お好み', shape: 'curry-plate', baseColor: '#7a4a2a', accentColor: '#e8c060' },
    reward: {
      unlockScore: 9000,
      area: '広島県',
      headline: '小麦粉の薄い生地にキャベツ、豚肉、焼きそば、卵を重ねて蒸し焼きにする。濃い甘口ソースと青のり。',
      tip: '広島は鉄を扱う工場が多く鉄板が手に入りやすかったことから、お好み焼屋が増えたと考えられている。県内の店舗数はおよそ1,300軒。',
      links: searchLinks('広島市', 'お好み焼き'),
    },
  },
  {
    id: 'yaki-gaki',
    level: 9,
    name: '焼きがき',
    shortName: '焼きがき',
    image: '/assets/foods/hiroshima/09_yaki_gaki.webp',
    radius: 57,
    mass: 6.6,
    score: 512,
    description: '殻ごと焼いて、口が開いたら。',
    source: 'https://www.miyajima.or.jp/gourmet/kaki.html',
    icon: { label: 'かき', shape: 'mochi', baseColor: '#c8bca0', accentColor: '#e8c48a' },
    reward: {
      unlockScore: 12000,
      area: '宮島・廿日市',
      headline: '宮島観光協会は「宮島かきは、おおぶりで味の良い上質な牡蠣」とし、広島は全国生産量のおよそ60パーセント以上を占めるとしている。',
      tip: '廿日市市だけで広島県のかき生産量のおよそ6分の1。地御前かき・大野瀬戸かき海道・宮島かきの3つのブランドがある。採苗から水揚げまで約1年。',
      links: searchLinks('宮島', '焼きがき'),
    },
  },
  {
    id: 'taimeshi',
    level: 10,
    name: '鯛めし',
    shortName: '鯛めし',
    image: '/assets/foods/hiroshima/10_taimeshi.webp',
    radius: 63,
    mass: 8,
    score: 1024,
    description: '釜に、鯛を丸ごと一尾。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/42_9_hiroshima.html',
    icon: { label: '鯛めし', shape: 'feast', baseColor: '#c1392b', accentColor: '#f4e6d2' },
    reward: {
      unlockScore: 16000,
      area: '鞆の浦・尾道',
      headline: '鯛の内臓とうろこを取って丸ごと釜に入れ、しょうゆ・酒・みりん・塩で味をつけて米と炊く。',
      tip: '江戸時代、走島の庄屋が鯛網の操業を始めたころから漁師たちが作るようになった。福山市鞆の浦の「鯛しばり網漁法」はおよそ380年前に始まり、いまは福山市の無形民俗文化財。',
      links: searchLinks('福山市鞆町', '鯛めし'),
    },
  },
  {
    id: 'anagomeshi',
    level: 11,
    name: 'あなごめし',
    shortName: 'あなごめし',
    image: '/assets/foods/hiroshima/11_anagomeshi.webp',
    radius: 70,
    mass: 9.6,
    score: 2048,
    description: 'だしで炊いた飯に、かば焼きを敷き詰める。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/42_30_hiroshima.html',
    icon: { label: 'あなご', shape: 'rice-bowl', baseColor: '#8a5a32', accentColor: '#f3e6c6' },
    reward: {
      unlockScore: 22000,
      area: '宮島口・廿日市',
      headline: '明治34年、山陽鉄道の開通から4年後に、宮島口駅の近くで駅弁として売られたのが始まり。',
      tip: '昭和30年代には「バス弁」として人気に。廿日市市の大野瀬戸あたりで獲れるものは「瀬戸のあなご」と呼ばれる。',
      links: searchLinks('廿日市市', 'あなごめし'),
    },
  },
  {
    id: 'kaki-dotenabe',
    level: 12,
    name: 'かきの土手鍋',
    shortName: '土手鍋',
    image: '/assets/foods/hiroshima/12_kaki_dotenabe.webp',
    radius: 78,
    mass: 11.4,
    score: 4096,
    description: '鍋のふちの味噌を、崩しながら。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/42_4_hiroshima.html',
    icon: { label: '土手鍋', shape: 'hotpot', baseColor: '#8a6a3a', accentColor: '#e8d8b8' },
    reward: {
      unlockScore: 30000,
      area: '広島・江田島・宮島',
      headline: '鍋のふちに塗った味噌を崩しながら味を調えて食べる。ほかの鍋にはない食べ方。',
      tip: '名前の由来は3つ伝わっている。味噌を土手のように塗るから、考案者が土手長吉さんだったから、かき船が川の土手で鍋を食べさせていたから。',
      links: searchLinks('広島市', 'かきの土手鍋'),
    },
  },
];

/** 得点は 2 ^ level を基準にしている（GAME_TUNING.score で方式変更可能）。 */
export const hiroshimaFoodStage: StageDefinition = {
  regionId: 'hiroshima',
  regionName: '広島県',
  mode: 'food',
  title: '広島めしマージ',
  theme: {
    primaryColor: '#8a3a3a',
    secondaryColor: '#d9a441',
    accentColor: '#f2d16b',
    backgroundColor: '#1a1615',
  },
  /** 背景ツアー「広島めぐり」。軸は季節。5月から翌4月へ一周する。 */
  scenery: {
    tour: hiroshimaSceneryTour,
  },
  items: attachHulls(FOOD_ITEMS, HIROSHIMA_FOOD_HULLS),
};
